import React, { useState, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { FiX, FiCamera } from 'react-icons/fi'
import { ErrorNotification, SuccessNotification } from '../utils'
import api, { BASE_URL } from '../utils/api'
import Spinner from '../components/Spinner'
import logomain from '../assets/logo-undeline.png'
import logotrans from '../assets/logo-trans.png'
import axios from 'axios'

const Support = () => {
  const history = useHistory()
  const [title, setTitle] = useState('')
  const [email, setEmail] = useState('')
  const [description, setDescription] = useState('')
  const [screenshot, setScreenshot] = useState(null)
  const [screenshotPreview, setScreenshotPreview] = useState(null)
  const [platform] = useState('web') // Default to web platform
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})
  const fileInputRef = useRef(null)

  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('theme')
    return stored === 'dark'
  })

  const validateFile = (file) => {
    const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Only JPG, JPEG, PNG, GIF, and WEBP files are allowed'
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 10MB'
    }
    return null
  }

  const handleScreenshotChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      const validationError = validateFile(file)
      if (validationError) {
        ErrorNotification(validationError)
        return
      }

      setScreenshot(file)

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setScreenshotPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveScreenshot = () => {
    setScreenshot(null)
    setScreenshotPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validation
    const newErrors = {}
    if (!title.trim()) newErrors.title = 'Title is required'
    if (!email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    if (!description.trim()) newErrors.description = 'Description is required'

    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0]
      const el = document.querySelector(`[name="${firstErrorKey}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    setSubmitting(true)
    try {
      const formData = new FormData()
      formData.append('email', email.trim())
      formData.append('title', title.trim())
      formData.append('description', description.trim())
      formData.append('platform', platform)
      if (screenshot) {
        formData.append('attach_image', screenshot)
      }

      await axios.post(`${BASE_URL}bug-reports/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      SuccessNotification('Bug report submitted successfully! We will review it and get back to you soon.')

      // Reset form
      setTitle('')
      setEmail('')
      setDescription('')
      setScreenshot(null)
      setScreenshotPreview(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (err) {
      ErrorNotification(err?.response?.data?.message || err?.response?.data?.detail || 'Failed to submit bug report. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark">
      {/* Header */}
      <div className="bg-white dark:bg-dark border-b border-gray-200 dark:border-border-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <img
              src={darkMode ? logotrans : logomain}
              alt="logo"
              className="h-10 object-contain cursor-pointer"
              onClick={() => history.push('/')}
            />
            <button
              onClick={() => history.push('/')}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-secondaryDark rounded-lg shadow-lg p-6 md:p-8 border border-gray-200 dark:border-border-dark">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Report a Bug
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Found a bug or issue? Please fill out the form below with details about the problem. Include a screenshot if possible to help us understand and fix it faster.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Issue Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white ${errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="e.g., Login button not responsive"
              />
              {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white ${errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="Enter your email address"
              />
              {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white ${errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="Describe the bug or issue in detail. Include steps to reproduce, what you expected to happen, and what actually happened. e.g., When I click the login button on the homepage, nothing happens. I'm using Chrome 120 on Windows 11."
              />
              {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
            </div>

            {/* Screenshot */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Screenshot (Optional)
              </label>
              {screenshotPreview ? (
                <div className="relative inline-block">
                  <img
                    src={screenshotPreview}
                    alt="Screenshot preview"
                    className="max-w-full max-h-64 rounded-md border border-gray-300 dark:border-border-dark"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveScreenshot}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                  >
                    <FiX size={18} />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 dark:border-border-dark rounded-md p-6 text-center hover:border-yellowcolor transition-colors">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleScreenshotChange}
                    className="hidden"
                    id="screenshot-upload"
                  />
                  <label
                    htmlFor="screenshot-upload"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <FiCamera className="text-gray-400 dark:text-gray-500" size={32} />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Click to upload or drag and drop
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-500">
                      PNG, JPG, GIF up to 10MB
                    </span>
                  </label>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={submitting}
                className={`flex-1 bg-yellowcolor hover:bg-yellowcolor/90 text-black font-medium py-3 px-6 rounded-md transition-colors ${submitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner />
                    Submitting...
                  </span>
                ) : (
                  'Submit Request'
                )}
              </button>
              <button
                type="button"
                onClick={() => history.push('/')}
                className="px-6 py-3 border border-gray-300 dark:border-border-dark text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-secondaryDark transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Support

